import { Photo } from '@/types';
import { PhotoCard } from './PhotoCard';
import { Upload, Image as ImageIcon, Loader2, Trash2, CheckSquare, Square, Tag, ChevronDown, Star, FolderPlus, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useState, useEffect } from 'react';
import { FullscreenImageViewer } from './FullscreenImageViewer';
import { formatNumber } from '@/utils/formatNumber';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from '@/components/ui/pagination';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface GalleryProps {
  photos: Photo[];
  selectedPhotoId: string | null;
  selectedPhotoIds: string[];
  onPhotoSelect: (photoId: string) => void;
  onPhotoToggle: (id: string) => void;
  onSelectAll: () => void;
  onSelectAllPhotos?: () => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  isDragging: boolean;
  onUploadClick: () => void;
  thumbnailSize?: 'small' | 'medium' | 'large';
  onThumbnailSizeChange?: (size: 'small' | 'medium' | 'large') => void;
  isLoading?: boolean;
  showEmptyTrash?: boolean;
  onEmptyTrash?: () => void;
  isEmptyingTrash?: boolean;
  onBulkDelete?: () => void;
  onBulkAddTags?: () => void;
  onBulkRestore?: () => void;
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  sortField?: 'created_at' | 'updated_at' | 'filename' | 'rating' | 'date_taken';
  sortDirection?: 'asc' | 'desc';
  onSortChange?: (field: 'created_at' | 'updated_at' | 'filename' | 'rating' | 'date_taken', direction: 'asc' | 'desc') => void;
  hasActiveFilters?: boolean;
}

export function Gallery({
  photos, 
  selectedPhotoId, 
  selectedPhotoIds,
  onPhotoSelect, 
  onPhotoToggle,
  onSelectAll,
  onSelectAllPhotos,
  onDrop, 
  onDragOver,
  isDragging,
  onUploadClick,
  thumbnailSize = 'medium',
  onThumbnailSizeChange,
  isLoading = false,
  showEmptyTrash = false,
  onEmptyTrash,
  isEmptyingTrash = false,
  onBulkDelete,
  onBulkAddTags,
  onBulkRestore,
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  itemsPerPage = 25,
  onPageChange,
  sortField = 'created_at',
  sortDirection = 'desc',
  onSortChange,
  hasActiveFilters = false
}: GalleryProps) {
  const allSelected = photos.length > 0 && selectedPhotoIds.length === photos.length;
  const [fullscreenPhotoId, setFullscreenPhotoId] = useState<string | null>(null);
  const [showEmptyState, setShowEmptyState] = useState(false);

  const fullscreenPhoto = fullscreenPhotoId ? photos.find(p => p.id === fullscreenPhotoId) : null;
  const currentIndex = fullscreenPhotoId ? photos.findIndex(p => p.id === fullscreenPhotoId) : -1;

  // Delay showing empty state on initial load
  useEffect(() => {
    if (photos.length === 0 && !isLoading) {
      const timer = setTimeout(() => {
        setShowEmptyState(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setShowEmptyState(false);
    }
  }, [photos.length, isLoading]);

  const handleNext = () => {
    if (currentIndex < photos.length - 1) {
      setFullscreenPhotoId(photos[currentIndex + 1].id);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setFullscreenPhotoId(photos[currentIndex - 1].id);
    }
  };

  // Map thumbnail size to grid columns classes
  // Mobile has specific pixel widths, desktop uses responsive columns
  const getGridClass = () => {
    const mapping: Record<'small' | 'medium' | 'large', string> = {
      small: 'grid-cols-2 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-9',
      medium: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
      large: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
    };
    return mapping[thumbnailSize] || mapping.medium;
  };

  const getGridStyle = (): React.CSSProperties | undefined => {
    // Only apply custom grid on mobile
    if (window.innerWidth >= 768) return undefined;
    
    const mobileStyles: Record<'small' | 'medium' | 'large', string> = {
      small: 'repeat(2, 165px)',
      medium: 'repeat(2, 1fr)',
      large: 'repeat(1, 365px)',
    };
    return {
      gridTemplateColumns: mobileStyles[thumbnailSize],
    };
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {photos.length > 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              className="h-9 gap-2 hidden md:flex"
              onClick={onSelectAll}
            >
              {allSelected ? <Square className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
              Select All
            </Button>
          )}
          {selectedPhotoIds.length > 0 && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="default"
                    size="sm" 
                    className="h-9 gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Bulk Actions
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 bg-popover z-50">
                  {showEmptyTrash && onBulkRestore ? (
                    <DropdownMenuItem onClick={onBulkRestore} className="cursor-pointer">
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Restore
                    </DropdownMenuItem>
                  ) : (
                    <>
                      <DropdownMenuItem onClick={onBulkAddTags} className="cursor-pointer">
                        <Tag className="h-4 w-4 mr-2" />
                        Add Tags
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => {}} className="cursor-pointer">
                        <Star className="h-4 w-4 mr-2" />
                        Set Rating
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => {}} className="cursor-pointer">
                        <FolderPlus className="h-4 w-4 mr-2" />
                        Add to Folder
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={onBulkDelete} className="cursor-pointer text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <div className="h-6 w-px bg-border" />
              <h2 className="text-lg font-semibold">
                {selectedPhotoIds.length} selected
              </h2>
            </>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {showEmptyTrash && onEmptyTrash && (
            <Button onClick={onEmptyTrash} size="sm" variant="destructive" disabled={isEmptyingTrash}>
              {isEmptyingTrash ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Empty Trash
                </>
              )}
            </Button>
          )}
          
          {/* Thumbnail Size Slider */}
          {onThumbnailSizeChange && (
            <div className="flex items-center gap-2 px-2">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Size</span>
              <Slider
                value={[thumbnailSize === 'small' ? 1 : thumbnailSize === 'medium' ? 2 : 3]}
                onValueChange={(value) => {
                  const size = value[0] === 1 ? 'small' : value[0] === 2 ? 'medium' : 'large';
                  onThumbnailSizeChange(size);
                }}
                min={1}
                max={3}
                step={1}
                className="w-24"
              />
            </div>
          )}
          
          {/* Sort controls */}
          {onSortChange && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-md border bg-background">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 px-2">
                    {sortField === 'created_at' && 'Created'}
                    {sortField === 'updated_at' && 'Modified'}
                    {sortField === 'filename' && 'Name'}
                    {sortField === 'rating' && 'Rating'}
                    {sortField === 'date_taken' && 'Date Taken'}
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40 bg-background z-50">
                  <DropdownMenuItem onClick={() => onSortChange('created_at', sortDirection)}>
                    Created
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onSortChange('updated_at', sortDirection)}>
                    Modified
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onSortChange('filename', sortDirection)}>
                    Name
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onSortChange('rating', sortDirection)}>
                    Rating
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onSortChange('date_taken', sortDirection)}>
                    Date Taken
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button 
                variant="ghost" 
                size="icon"
                className="h-7 w-7"
                onClick={() => onSortChange(sortField, sortDirection === 'asc' ? 'desc' : 'asc')}
                title={sortDirection === 'asc' ? 'Sort Ascending' : 'Sort Descending'}
              >
                {sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
              </Button>
            </div>
          )}
          
          <Button onClick={onUploadClick} size="sm">
            <Upload className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Upload</span>
          </Button>
        </div>
      </div>

      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        className={`
          flex-1 overflow-y-auto p-6
          ${isDragging ? 'bg-primary/10 border-2 border-dashed border-primary' : ''}
        `}
      >
      {photos.length === 0 && showEmptyState ? (
        <div className="h-full flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="p-6 bg-card rounded-2xl border-2 border-dashed border-border">
                {isDragging ? (
                  <Upload className="w-16 h-16 text-primary animate-bounce" />
                ) : (
                  <ImageIcon className="w-16 h-16 text-muted-foreground" />
                )}
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">
                {isDragging ? 'Drop your photos here' : hasActiveFilters ? 'No Photos Found' : 'No photos yet'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {isDragging 
                  ? 'Release to upload'
                  : hasActiveFilters
                    ? 'Try adjusting your search or filters'
                    : 'Drag and drop photos anywhere to get started'
                }
              </p>
              {!hasActiveFilters && (
                <button
                  onClick={onUploadClick}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
                >
                  Choose Files
                </button>
              )}
            </div>
          </div>
        </div>
        ) : photos.length === 0 || isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center space-y-4">
              <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto" />
              <p className="text-muted-foreground">Loading photos...</p>
            </div>
          </div>
        ) : (
          <div className={`grid ${getGridClass()} gap-4`} style={getGridStyle()}>
            {photos.map((photo) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                isSelected={selectedPhotoIds.includes(photo.id)}
                onSelect={onPhotoToggle}
                onClick={() => onPhotoSelect(photo.id)}
                onDoubleClick={() => setFullscreenPhotoId(photo.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination at bottom - hidden on mobile */}
      {photos.length > 0 && (
        <div className="hidden md:flex p-4 border-t border-border items-center justify-between flex-shrink-0">
          {/* Left: Entry count - 50% */}
          <div className="text-sm text-muted-foreground flex-1">
            Showing {formatNumber(((currentPage - 1) * itemsPerPage) + 1)} to {formatNumber(Math.min(currentPage * itemsPerPage, totalCount))} of {formatNumber(totalCount)} entries
          </div>

          {/* Right: Pagination - 50% with right alignment */}
          <div className="flex-1 flex justify-end">
            <Pagination className="ml-auto w-auto justify-end">
              <PaginationContent>
                <PaginationItem>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onPageChange?.(1)}
                    disabled={currentPage <= 1}
                    className="h-9 w-9"
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                </PaginationItem>
                
                <PaginationItem>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1}
                    className="h-9 w-9"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </PaginationItem>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        onClick={() => onPageChange?.(pageNum)}
                        isActive={currentPage === pageNum}
                        className="cursor-pointer"
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                
                <PaginationItem>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onPageChange?.(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                    className="h-9 w-9"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </PaginationItem>
                
                <PaginationItem>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onPageChange?.(totalPages)}
                    disabled={currentPage >= totalPages}
                    className="h-9 w-9"
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      )}

      {/* Fullscreen viewer */}
      {fullscreenPhoto && (
        <FullscreenImageViewer
          photo={fullscreenPhoto}
          onClose={() => setFullscreenPhotoId(null)}
          onNext={handleNext}
          onPrevious={handlePrevious}
          hasNext={currentIndex < photos.length - 1}
          hasPrevious={currentIndex > 0}
          onPhotoUpdate={(updatedPhoto) => {
            // Update the photo in the photos array if needed
            const photoIndex = photos.findIndex(p => p.id === updatedPhoto.id);
            if (photoIndex !== -1) {
              photos[photoIndex] = updatedPhoto;
            }
          }}
        />
      )}
    </div>
  );
}
